import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';

const seniorProducts = [
  {
    id: 'final-expense',
    name: 'Final Expense Insurance',
    icon: 'ri-shield-line',
    ageRange: '50–85',
    coverage: '$5,000–$35,000',
    approval: '24–48 hours',
    exam: false,
    bestFor: 'Covering burial, funeral & end-of-life costs',
    carriers: ['Mutual of Omaha', 'Foresters', 'Transamerica'],
    startingRate: '$28/mo',
    highlight: true,
    badge: 'Most Popular for Seniors',
  },
  {
    id: 'guaranteed-issue',
    name: 'Guaranteed Issue Whole Life',
    icon: 'ri-checkbox-circle-line',
    ageRange: '50–80',
    coverage: '$2,000–$25,000',
    approval: 'Instant — no questions',
    exam: false,
    bestFor: 'Any health condition — guaranteed acceptance',
    carriers: ['Gerber Life', 'AIG', 'Mutual of Omaha'],
    startingRate: '$42/mo',
    highlight: false,
    badge: 'No Health Questions',
  },
  {
    id: 'simplified-issue',
    name: 'Simplified Issue Whole Life',
    icon: 'ri-file-list-3-line',
    ageRange: '50–75',
    coverage: '$10,000–$100,000',
    approval: '24–72 hours',
    exam: false,
    bestFor: 'Better rates for those in fair-to-good health',
    carriers: ['North American', 'Foresters', 'Americo'],
    startingRate: '$35/mo',
    highlight: false,
    badge: 'Best Value',
  },
  {
    id: 'term-senior',
    name: 'Senior Term Life',
    icon: 'ri-time-line',
    ageRange: '60–75',
    coverage: '$50,000–$500,000',
    approval: '3–7 days',
    exam: false,
    bestFor: 'Income replacement, mortgage payoff, legacy',
    carriers: ['Protective', 'Banner Life', 'Pacific Life'],
    startingRate: '$62/mo',
    highlight: false,
    badge: 'Highest Coverage',
  },
];

const coverageFinderResults: Record<string, Record<string, { product: string; carrier: string; rate: string; coverage: string; approval: string; note: string }[]>> = {
  '60-64': {
    excellent: [
      { product: 'Simplified Issue Whole Life', carrier: 'North American', rate: '$35/mo', coverage: '$50,000', approval: '24 hrs', note: 'Best rate for your age & health' },
      { product: 'Senior Term Life (20-yr)', carrier: 'Protective Life', rate: '$62/mo', coverage: '$250,000', approval: '3–5 days', note: 'Maximum income replacement' },
      { product: 'Final Expense', carrier: 'Mutual of Omaha', rate: '$28/mo', coverage: '$20,000', approval: '24 hrs', note: 'Covers all burial costs' },
    ],
    good: [
      { product: 'Final Expense', carrier: 'Foresters Financial', rate: '$34/mo', coverage: '$20,000', approval: '48 hrs', note: 'Most popular choice' },
      { product: 'Simplified Issue Whole Life', carrier: 'Americo', rate: '$48/mo', coverage: '$35,000', approval: '48 hrs', note: 'Good health discount applied' },
      { product: 'Senior Term Life (15-yr)', carrier: 'Banner Life', rate: '$78/mo', coverage: '$150,000', approval: '5–7 days', note: 'Strong legacy option' },
    ],
    fair: [
      { product: 'Final Expense', carrier: 'Transamerica', rate: '$44/mo', coverage: '$15,000', approval: '48 hrs', note: 'Covers burial & final bills' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'Mutual of Omaha', rate: '$52/mo', coverage: '$15,000', approval: 'Instant', note: 'No health questions asked' },
    ],
    serious: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', rate: '$58/mo', coverage: '$10,000', approval: 'Instant', note: 'Guaranteed — no exam, no questions' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'AIG', rate: '$62/mo', coverage: '$12,000', approval: 'Instant', note: 'Accepted regardless of health' },
    ],
  },
  '65-69': {
    excellent: [
      { product: 'Final Expense', carrier: 'Mutual of Omaha', rate: '$38/mo', coverage: '$20,000', approval: '24 hrs', note: 'Top-rated carrier, best price' },
      { product: 'Simplified Issue Whole Life', carrier: 'North American', rate: '$52/mo', coverage: '$35,000', approval: '48 hrs', note: 'Strong cash value growth' },
      { product: 'Senior Term Life (10-yr)', carrier: 'Protective Life', rate: '$88/mo', coverage: '$150,000', approval: '3–5 days', note: 'Legacy & income protection' },
    ],
    good: [
      { product: 'Final Expense', carrier: 'Foresters Financial', rate: '$46/mo', coverage: '$15,000', approval: '48 hrs', note: 'Most popular for this age group' },
      { product: 'Simplified Issue Whole Life', carrier: 'Americo', rate: '$64/mo', coverage: '$25,000', approval: '48 hrs', note: 'No exam required' },
    ],
    fair: [
      { product: 'Final Expense', carrier: 'Transamerica', rate: '$58/mo', coverage: '$12,000', approval: '48 hrs', note: 'Covers burial costs' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'Mutual of Omaha', rate: '$68/mo', coverage: '$12,000', approval: 'Instant', note: 'No health questions' },
    ],
    serious: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'AIG', rate: '$74/mo', coverage: '$10,000', approval: 'Instant', note: 'Guaranteed acceptance' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', rate: '$78/mo', coverage: '$10,000', approval: 'Instant', note: 'No exam, no questions' },
    ],
  },
  '70-74': {
    excellent: [
      { product: 'Final Expense', carrier: 'Mutual of Omaha', rate: '$52/mo', coverage: '$15,000', approval: '24 hrs', note: 'Best rate for your age' },
      { product: 'Simplified Issue Whole Life', carrier: 'Foresters', rate: '$74/mo', coverage: '$20,000', approval: '48 hrs', note: 'No exam required' },
    ],
    good: [
      { product: 'Final Expense', carrier: 'Transamerica', rate: '$62/mo', coverage: '$12,000', approval: '48 hrs', note: 'Covers burial & final expenses' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'Mutual of Omaha', rate: '$78/mo', coverage: '$10,000', approval: 'Instant', note: 'No health questions' },
    ],
    fair: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'AIG', rate: '$84/mo', coverage: '$10,000', approval: 'Instant', note: 'Guaranteed acceptance' },
    ],
    serious: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', rate: '$92/mo', coverage: '$8,000', approval: 'Instant', note: 'Guaranteed — no questions' },
    ],
  },
  '75-80': {
    excellent: [
      { product: 'Final Expense', carrier: 'Mutual of Omaha', rate: '$72/mo', coverage: '$10,000', approval: '48 hrs', note: 'Best available rate' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'AIG', rate: '$88/mo', coverage: '$10,000', approval: 'Instant', note: 'No health questions' },
    ],
    good: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Mutual of Omaha', rate: '$94/mo', coverage: '$10,000', approval: 'Instant', note: 'Guaranteed acceptance' },
    ],
    fair: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', rate: '$102/mo', coverage: '$8,000', approval: 'Instant', note: 'No exam, no questions' },
    ],
    serious: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', rate: '$108/mo', coverage: '$7,500', approval: 'Instant', note: 'Guaranteed — any health condition' },
    ],
  },
};

const faqs = [
  { q: 'Can seniors over 70 still get life insurance in Indianapolis?', a: 'Yes. Many carriers offer final expense and guaranteed issue whole life insurance up to age 80 or 85. Guaranteed issue policies accept any health condition with no medical exam or health questions.' },
  { q: 'What is the difference between final expense and guaranteed issue life insurance?', a: 'Final expense insurance has a few health questions but offers better rates and higher coverage ($5K–$35K). Guaranteed issue has no health questions at all but costs more and has lower coverage limits ($2K–$25K). Both are permanent whole life policies.' },
  { q: 'How much does senior life insurance cost in Indianapolis?', a: 'Final expense policies for Indianapolis seniors typically start at $28–$45/month for $10,000–$20,000 in coverage. Guaranteed issue policies start around $42–$60/month. Rates depend on age, health, and coverage amount.' },
  { q: 'Is there a waiting period for senior life insurance?', a: 'Final expense and simplified issue policies typically have no waiting period for accidental death and a 2-year graded benefit for natural causes. Guaranteed issue policies usually have a 2-year waiting period for full benefits.' },
  { q: 'What carriers offer the best senior life insurance in Indiana?', a: 'Top carriers for Indiana seniors include Mutual of Omaha, Foresters Financial, Transamerica, AIG, Gerber Life, and Americo. As an independent agent, we compare all of these to find your best rate.' },
  { q: 'Can I get life insurance if I have diabetes, heart disease, or COPD?', a: 'Yes. Guaranteed issue whole life insurance accepts any health condition with no questions asked. Final expense insurance may also be available depending on how well-controlled your condition is. We\'ll find the right fit.' },
  { q: 'How quickly can a senior get approved for life insurance?', a: 'Guaranteed issue policies approve instantly. Final expense and simplified issue policies typically approve within 24–72 hours. No medical exam is required for any of these products.' },
  { q: 'Does Medicare cover burial and funeral costs?', a: 'No. Medicare does not cover burial, funeral, or final expense costs. A final expense life insurance policy is specifically designed to cover these costs so your family isn\'t left with the bill.' },
  { q: 'What is the maximum coverage amount for senior life insurance?', a: 'Final expense policies typically go up to $35,000. Simplified issue whole life can go up to $100,000. Senior term life (for those 60–75 in good health) can provide $50,000–$500,000 in coverage.' },
  { q: 'How do I get a senior life insurance quote in Indianapolis?', a: 'Call us at (317) 648-2983 or submit the form on this page. As an independent agent, Benjamin Wright compares 20+ carriers to find your best rate — at no cost and no obligation.' },
];

export default function SeniorLifeInsuranceIndianapolisPage() {
  const [ageGroup, setAgeGroup] = useState('');
  const [healthClass, setHealthClass] = useState('');
  const [results, setResults] = useState<{ product: string; carrier: string; rate: string; coverage: string; approval: string; note: string }[] | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    document.title = 'Senior Life Insurance Indianapolis IN | Final Expense & Guaranteed Issue | Hoosier Life';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Senior life insurance in Indianapolis, Indiana. Final expense, guaranteed issue & simplified issue policies for ages 60–80. No medical exam. Approved in 24 hours. Compare 20+ carriers — free.');
    const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', `${SITE_URL}/senior-life-insurance-indianapolis`);
    if (!document.querySelector('link[rel="canonical"]')) document.head.appendChild(canonical);

    const speakable = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/senior-life-insurance-indianapolis#speakable`,
      speakable: {
        '@type': 'SpeakableSpecification',
        xpath: ['/html/body//*[@id="senior-indy-rates"]'],
      },
      url: `${SITE_URL}/senior-life-insurance-indianapolis`,
    };

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/senior-life-insurance-indianapolis#webpage`,
          url: `${SITE_URL}/senior-life-insurance-indianapolis`,
          name: 'Senior Life Insurance Indianapolis IN | Final Expense & Guaranteed Issue | Hoosier Life',
          description: 'Senior life insurance in Indianapolis, Indiana. Final expense, guaranteed issue & simplified issue policies for ages 60–80. No medical exam. Approved in 24 hours.',
          dateModified: '2026-04-15',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Senior Life Insurance Indianapolis', item: `${SITE_URL}/senior-life-insurance-indianapolis` },
            ],
          },
        },
        {
          '@type': 'Service',
          name: 'Senior Life Insurance Indianapolis',
          provider: { '@type': 'LocalBusiness', name: 'Hoosier Life Insurance', address: { '@type': 'PostalAddress', addressLocality: 'Indianapolis', addressRegion: 'IN' } },
          areaServed: { '@type': 'City', name: 'Indianapolis' },
          description: 'Final expense, guaranteed issue, and simplified issue life insurance for Indianapolis seniors ages 60–80.',
        },
        {
          '@type': 'FAQPage',
          mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
      ],
    };
    const existing = document.getElementById('senior-indy-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'senior-indy-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    const existingSpeakable = document.getElementById('senior-indy-speakable');
    if (existingSpeakable) existingSpeakable.remove();
    const speakableScript = document.createElement('script');
    speakableScript.id = 'senior-indy-speakable';
    speakableScript.type = 'application/ld+json';
    speakableScript.textContent = JSON.stringify(speakable);
    document.head.appendChild(speakableScript);

    return () => { script.remove(); speakableScript.remove(); };
  }, []);

  const handleFindCoverage = () => {
    if (!ageGroup || !healthClass) return;
    const res = coverageFinderResults[ageGroup]?.[healthClass] || [];
    setResults(res);
  };

  const healthLabels: Record<string, string> = {
    excellent: 'Excellent / Preferred',
    good: 'Good / Standard',
    fair: 'Fair / Some conditions',
    serious: 'Serious / Multiple conditions',
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />
      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d4f778e8a15c910f802fb7758f18070f.jpg"
            alt="Senior couple protected by life insurance in Indianapolis Indiana"
            className="w-full h-full object-cover object-top"
            width={1440}
            height={700}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2A3F]/90 via-[#1A3A52]/80 to-[#0F2A3F]/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-8">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Senior Life Insurance Indianapolis</span>
          </nav>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D97642]/25 text-[#F0A070] text-xs font-bold uppercase tracking-widest rounded-full border border-[#D97642]/40 mb-5">
              <i className="ri-shield-check-fill"></i>
              Indianapolis &amp; Central Indiana · Ages 60–80
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-[56px] font-extrabold text-white leading-[1.1] mb-6">
              Senior Life Insurance<br />
              <span className="text-[#F0A070]">Indianapolis, Indiana</span>
            </h1>
            <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-2xl">
              Final expense, guaranteed issue, and simplified issue life insurance for Indianapolis seniors ages 60–80. <strong className="text-white">No medical exam required.</strong> Approved in as little as 24 hours. We compare 20+ top-rated carriers to find your best rate — free.
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              {[
                { icon: 'ri-checkbox-circle-fill', label: 'No Medical Exam Required' },
                { icon: 'ri-timer-flash-line', label: 'Approved in 24–48 Hours' },
                { icon: 'ri-heart-pulse-line', label: 'Any Health Condition Welcome' },
                { icon: 'ri-map-pin-line', label: 'Local Indianapolis Agent' },
              ].map(b => (
                <div key={b.label} className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                  <i className={`${b.icon} text-[#D97642] text-sm`}></i>
                  <span className="text-sm text-white/90 font-medium">{b.label}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white font-bold rounded-full hover:bg-[#C26638] transition-all whitespace-nowrap">
                <i className="ri-calendar-check-fill text-lg"></i>
                Get My Free Senior Quote
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-bold rounded-full hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
              <button
                onClick={() => {
                  const widget = document.querySelector('readdy-agent') as HTMLElement & { open?: () => void };
                  if (widget && typeof widget.open === 'function') {
                    widget.open();
                  } else {
                    const btn = document.querySelector('.readdy-agent-button') as HTMLElement;
                    if (btn) btn.click();
                  }
                }}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/15 hover:bg-white/25 border border-white/30 hover:border-white/50 text-white font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-chat-3-line text-lg"></i>
                Chat with us
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section id="senior-indy-rates" className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '20+', label: 'Carriers Compared' },
              { value: '24 hrs', label: 'Average Approval Time' },
              { value: '$28/mo', label: 'Starting Rate' },
              { value: 'Ages 50–85', label: 'Eligibility Range' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{s.value}</div>
                <div className="text-sm text-white/70 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Senior Coverage Finder Tool */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-[#D97642]/15 text-[#D97642] text-xs font-bold uppercase tracking-widest rounded-full mb-4">Interactive Tool</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A3A52] mb-4">Senior Coverage Finder</h2>
            <p className="text-gray-600 max-w-xl mx-auto">Tell us your age and health situation — we'll show you exactly which senior life insurance products you qualify for and what they cost.</p>
          </div>

          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-bold text-[#1A3A52] mb-3">Your Age Group</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: '60-64', label: 'Ages 60–64' },
                    { value: '65-69', label: 'Ages 65–69' },
                    { value: '70-74', label: 'Ages 70–74' },
                    { value: '75-80', label: 'Ages 75–80' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setAgeGroup(opt.value)}
                      className={`px-4 py-3 rounded-lg text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${ageGroup === opt.value ? 'border-[#D97642] bg-[#D97642]/10 text-[#D97642]' : 'border-gray-200 text-gray-600 hover:border-[#D97642]/50'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#1A3A52] mb-3">Your Health Situation</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { value: 'excellent', label: 'Excellent — No major conditions' },
                    { value: 'good', label: 'Good — 1–2 managed conditions' },
                    { value: 'fair', label: 'Fair — Several health issues' },
                    { value: 'serious', label: 'Serious — Multiple conditions' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setHealthClass(opt.value)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all cursor-pointer text-left ${healthClass === opt.value ? 'border-[#D97642] bg-[#D97642]/10 text-[#D97642]' : 'border-gray-200 text-gray-600 hover:border-[#D97642]/50'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={handleFindCoverage}
              disabled={!ageGroup || !healthClass}
              className="w-full py-4 bg-[#D97642] text-white font-bold rounded-xl hover:bg-[#C26638] transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-base"
            >
              <i className="ri-search-line mr-2"></i>
              Find My Senior Coverage Options
            </button>

            {results !== null && (
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                  <h3 className="text-lg font-bold text-[#1A3A52]">
                    {results.length} Options Found — {ageGroup.replace('-', '–')} · {healthLabels[healthClass]}
                  </h3>
                </div>
                <div className="space-y-3">
                  {results.map((r, i) => (
                    <div key={i} className={`p-5 rounded-xl border-2 ${i === 0 ? 'border-[#D97642] bg-[#FFF5EF]' : 'border-gray-100 bg-gray-50'}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          {i === 0 && <span className="inline-block px-2 py-0.5 bg-[#D97642] text-white text-xs font-bold rounded-full mb-2">Best Match</span>}
                          <div className="font-bold text-[#1A3A52]">{r.product}</div>
                          <div className="text-sm text-gray-500 mt-0.5">{r.carrier} · Approved in {r.approval}</div>
                          <div className="text-xs text-[#D97642] font-semibold mt-1">{r.note}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-extrabold text-[#D97642]">{r.rate}</div>
                          <div className="text-sm text-gray-500">{r.coverage} coverage</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-[#1A3A52] rounded-xl text-center">
                  <p className="text-white text-sm mb-3">These are estimated rates. Get your exact personalized quote — free, no obligation.</p>
                  <a href="/book-appointment" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap">
                    <i className="ri-calendar-check-fill"></i>
                    Get My Exact Senior Rate
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Product Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A3A52] mb-4">Senior Life Insurance Options in Indianapolis</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Four types of coverage designed specifically for Indianapolis seniors. No medical exam required for any of them.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {seniorProducts.map(p => (
              <div key={p.id} className={`rounded-2xl p-7 border-2 relative ${p.highlight ? 'border-[#D97642] bg-[#FFF5EF]' : 'border-gray-100 bg-[#F9F7F4]'}`}>
                {p.badge && (
                  <span className={`absolute top-5 right-5 px-3 py-1 text-xs font-bold rounded-full ${p.highlight ? 'bg-[#D97642] text-white' : 'bg-[#1A3A52] text-white'}`}>{p.badge}</span>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D97642]/15 rounded-xl">
                    <i className={`${p.icon} text-[#D97642] text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1A3A52]">{p.name}</h3>
                    <div className="text-sm text-gray-500">Ages {p.ageRange}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Coverage</div>
                    <div className="text-sm font-bold text-[#1A3A52]">{p.coverage}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Starting Rate</div>
                    <div className="text-sm font-bold text-[#D97642]">{p.startingRate}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Approval Time</div>
                    <div className="text-sm font-bold text-[#1A3A52]">{p.approval}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">Medical Exam</div>
                    <div className="text-sm font-bold text-green-600">{p.exam ? 'Required' : 'Not Required'}</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4"><strong>Best for:</strong> {p.bestFor}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {p.carriers.map(c => (
                    <span key={c} className="px-2.5 py-1 bg-white border border-gray-200 text-xs font-semibold text-gray-600 rounded-full">{c}</span>
                  ))}
                </div>
                <a href="/book-appointment" className="block text-center py-3 bg-[#1A3A52] text-white font-bold rounded-lg hover:bg-[#0F2A3F] transition-all whitespace-nowrap">
                  Get {p.name} Quote
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs Senior Life Insurance */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A3A52] mb-4">Who Needs Senior Life Insurance in Indianapolis?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-heart-line', title: 'Covering Burial Costs', desc: 'The average funeral in Indianapolis costs $8,000–$12,000. A final expense policy ensures your family isn\'t left with that bill.' },
              { icon: 'ri-home-heart-line', title: 'Leaving a Legacy', desc: 'Want to leave something behind for your children or grandchildren? Even a $25,000 policy creates a meaningful inheritance.' },
              { icon: 'ri-bill-line', title: 'Paying Off Debts', desc: 'Credit cards, medical bills, or a remaining mortgage balance — life insurance ensures these don\'t become your family\'s burden.' },
              { icon: 'ri-hospital-line', title: 'Any Health Condition', desc: 'Diabetes, heart disease, COPD, cancer history — guaranteed issue policies accept any health condition, no questions asked.' },
              { icon: 'ri-user-heart-line', title: 'Protecting a Spouse', desc: 'If your spouse depends on your Social Security or pension income, life insurance replaces that income when you\'re gone.' },
              { icon: 'ri-time-line', title: 'No Coverage Yet', desc: 'It\'s never too late. Indianapolis seniors up to age 85 can still get meaningful life insurance coverage starting at $28/month.' },
            ].map(item => (
              <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642]/10 rounded-xl mb-4">
                  <i className={`${item.icon} text-[#D97642] text-2xl`}></i>
                </div>
                <h3 className="text-base font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Carrier Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A3A52] mb-4">Top Senior Life Insurance Carriers in Indiana</h2>
            <p className="text-gray-600">We compare all of these — you get the best rate without shopping around.</p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  <th className="px-5 py-4 text-left font-bold">Carrier</th>
                  <th className="px-5 py-4 text-left font-bold">AM Best</th>
                  <th className="px-5 py-4 text-left font-bold">Max Age</th>
                  <th className="px-5 py-4 text-left font-bold">Max Coverage</th>
                  <th className="px-5 py-4 text-left font-bold">Approval</th>
                  <th className="px-5 py-4 text-left font-bold">Specialty</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { carrier: 'Mutual of Omaha', rating: 'A+', maxAge: 85, maxCov: '$40,000', approval: '24 hrs', specialty: 'Final Expense' },
                  { carrier: 'Foresters Financial', rating: 'A', maxAge: 80, maxCov: '$35,000', approval: '48 hrs', specialty: 'Final Expense' },
                  { carrier: 'Transamerica', rating: 'A', maxAge: 80, maxCov: '$50,000', approval: '48 hrs', specialty: 'Simplified Issue' },
                  { carrier: 'AIG / American General', rating: 'A', maxAge: 80, maxCov: '$25,000', approval: 'Instant', specialty: 'Guaranteed Issue' },
                  { carrier: 'Gerber Life', rating: 'A', maxAge: 80, maxCov: '$25,000', approval: 'Instant', specialty: 'Guaranteed Issue' },
                  { carrier: 'Americo', rating: 'A', maxAge: 75, maxCov: '$100,000', approval: '48 hrs', specialty: 'Simplified Issue' },
                ].map((row, i) => (
                  <tr key={row.carrier} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-5 py-4 font-semibold text-[#1A3A52]">{row.carrier}</td>
                    <td className="px-5 py-4 text-green-600 font-bold">{row.rating}</td>
                    <td className="px-5 py-4 text-gray-700">Age {row.maxAge}</td>
                    <td className="px-5 py-4 text-gray-700">{row.maxCov}</td>
                    <td className="px-5 py-4 text-gray-700">{row.approval}</td>
                    <td className="px-5 py-4"><span className="px-2.5 py-1 bg-[#D97642]/10 text-[#D97642] text-xs font-bold rounded-full">{row.specialty}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A3A52] mb-4">Senior Life Insurance FAQ — Indianapolis</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                >
                  <span className="font-semibold text-[#1A3A52] pr-4 text-sm">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-[#D97642] text-xl flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1A3A52]">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">Get Your Free Senior Life Insurance Quote Today</h2>
          <p className="text-white/75 mb-8 text-lg">Indianapolis seniors — we compare 20+ carriers to find your best rate. No exam. No pressure. No cost.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white font-bold rounded-full hover:bg-[#C26638] transition-all whitespace-nowrap">
              <i className="ri-calendar-check-fill text-lg"></i>
              Book My Free Consultation
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-bold rounded-full hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              Call (317) 648-2983
            </a>
          </div>
          <p className="mt-6 text-white/40 text-xs">Licensed Indiana Agent · Benjamin Wright · No spam · Your info is never sold</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
